import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Transition } from 'react-transition-group';
import { Button, Col, Container, Div, Row, Text } from '@pinpoint/design-kit';

import { pxScoresPropType } from 'src/PropTypes';
import { Style1 as Style } from 'src/styles';

const duration = 300;

const defaultStyle = {
  transition: `opacity ${duration}ms ease-in-out`,
  opacity: 0,
  position: 'absolute',
};

const defaultTransitionStyles = {
  entering: { opacity: 0 },
  entered: { opacity: 1 },
  exiting: { opacity: 0 },
  exited: { opacity: 0 },
};

function HoverHelp({ hoveredOn }) {
  return (
    <>
      <Transition in={!hoveredOn} timeout={duration}>
        {(state) => (
          <Div
            style={{
              ...defaultStyle,
              ...defaultTransitionStyles[state],
            }}
          >
            <Text textSize="body">Please place your cursor over the left pane to learn about each section</Text>
          </Div>
        )}
      </Transition>
      <Transition in={hoveredOn === 'DATASET'} timeout={duration}>
        {(state) => (
          <Div
            style={{
              ...defaultStyle,
              ...defaultTransitionStyles[state],
            }}
          >
            <Text textWeight="300" p="2rem" textSize="heading" textAlign="justify">
              A dataset is composed of the totality of Matched users (users found on Pinpoint&apos;s anonymized General
              Population data), data dimension qualifiers for engagement behaviour with your business (such as LTV or
              number of orders placed), and their personality scores across the Factors and Facets of the Big5 Model.
              This is the input of the Personality Segmentation Analysis.
            </Text>
          </Div>
        )}
      </Transition>
      <Transition in={hoveredOn === 'ENGAGEMENT'} timeout={duration}>
        {(state) => (
          <Div
            style={{
              ...defaultStyle,
              ...defaultTransitionStyles[state],
            }}
          >
            <Text textWeight="300" p="2rem" textSize="heading" textAlign="justify">
              Engagement Segments are Dataset slices made of users that have exhibited one specific engagement with your
              business - such as all users with higher LTV than the group average, users that qualify as unconverted
              leads, or users that have purchased an specific product. Typically, Engagement Segments will contain users
              from different personality scores and profiles, and thus not have a very high Singularity Score.
            </Text>
          </Div>
        )}
      </Transition>
      <Transition in={hoveredOn === 'PERSONALITY'} timeout={duration}>
        {(state) => (
          <Div
            style={{
              ...defaultStyle,
              ...defaultTransitionStyles[state],
            }}
          >
            <Text textWeight="300" p="2rem" textSize="heading" textAlign="justify">
              Personality Segments are isolated subgroups of users within the analyzed Dataset where all users score
              very closely across all the facets of personality, thus representing an distinct personality profile
              relative to the rest of the users in the dataset. Personality Segments have a high Singularity Score.
            </Text>
          </Div>
        )}
      </Transition>
    </>
  );
}

HoverHelp.propTypes = {
  hoveredOn: PropTypes.bool.isRequired,
};

const isPersonalitySegment = (filter) => filter.startsWith('CLUSTERING');

const isDatasetSegment = (filter) => filter === 'POPULATION_SNAPSHOT';

const categorizeSegments = (segments) => {
  const segmentsCategory = {
    PERSONALITY: [],
    ENGAGEMENT: [],
    DATASET: undefined,
  };
  for (let i = 0; i < segments.length; i += 1) {
    const { filter } = segments[i];
    if (isPersonalitySegment(filter)) {
      segmentsCategory.PERSONALITY.push(segments[i]);
    } else if (isDatasetSegment(filter)) {
      segmentsCategory.DATASET = segments[i];
    } else {
      segmentsCategory.ENGAGEMENT.push(segments[i]);
    }
  }
  return segmentsCategory;
};

function Datasets({ segments, styleOverride, minW, minH }) {
  const [hoveredOn, hoverOn] = useState('');

  const segmentsCategory = useMemo(() => categorizeSegments(segments), [segments]);

  const numEngagementSegments = segmentsCategory.ENGAGEMENT.length;
  const numPersonalitySegments = segmentsCategory.PERSONALITY.length;

  const { dataset, users, matched, score } = segmentsCategory.DATASET;

  const heading = `${dataset.name} Analysis Summary`;

  let line1 = '';
  let line2 = '';

  if (numPersonalitySegments === 0) {
    line1 =
      'The dataset contains no personality segments. This indicates segmention analysis is not yet performed on this dataset';
  } else {
    line1 = `Personality Segmentation Analysis on the provided ${dataset.name} revealed several Personality Segments showing different degrees of affinity with all or some of the user provided user Engagements, based on concentration of engaged users within each one.`;
    line2 =
      'Users within a Personality Segment can exhibit any engagement behavior, but they will be more prone to show the specific one that was deemed highly correlated with the personality profile. Similarly, any given user can belong to any number of Engagement Segments - but it will belong to only one Personality Segment.';
  }

  return (
    <Container
      h="100%"
      minH={minH}
      w="100%"
      minW={minW}
      bg="secondary"
      style={{
        paddingLeft: '0rem',
        paddingRight: '0rem',
      }}
    >
      <Row p={{ t: '1rem', b: '0.5rem' }} minH="40px">
        <Col align="center" justify="center" size={{ xs: '12' }} h="40px">
          <Text fontFamily="secondary" textSize="title" textWeight="bold" textAlign="center">
            {heading}
          </Text>
        </Col>
      </Row>
      <Row minH="150px">
        <Col size={{ xs: '12' }}>
          <Div minH="150px" h="100%" w="100%" rounded={{ lg: 'lg', xs: '0' }} style={styleOverride} p="1rem">
            <Text textWeight="300" p="0.5rem" textSize="body">
              {line1}
            </Text>
            <Text textWeight="300" p="0.5rem" textSize="body">
              {line2}
            </Text>
          </Div>
        </Col>
      </Row>
      <Row h="calc(100% - 40px - 150px - 1.5rem)" maxH="calc(100% - 40px - 150px - 1.5rem)">
        <Col size={{ xs: '6', lg: '4' }} h="100%">
          <Div
            pos="relative"
            maxH="calc(33% - 1.3rem)"
            h="calc(33% - 1.3rem)"
            w="100%"
            border="2px solid"
            bg="white"
            borderColor="white"
            hoverBg="gray100"
            hoverBorderColor="brand100"
            rounded={{ lg: 'lg', xs: '0' }}
            p={{ y: '1.25rem', x: '1.5rem' }}
            m={{ y: '1rem' }}
            cursor="pointer"
            overflow="auto"
            onMouseEnter={() => {
              hoverOn('DATASET');
            }}
            onMouseLeave={() => {
              hoverOn('');
            }}
          >
            <Div d="flex" align="center">
              <Text fontFamily="secondary" textSize="title" textWeight="bold" p={{ y: '0.25rem' }} tag="span">
                Dataset Details
              </Text>
              <Button w="2rem" h="2rem" tag="span" bg="info500" rounded="circle" m={{ x: '1rem' }} />
            </Div>
            <Text textWeight="300" p="0.5rem" textSize="body">
              Users: {users || '-'}
            </Text>
            <Text textWeight="300" p="0.5rem" textSize="body">
              Matched: {matched || '-'}
            </Text>
            <Text textWeight="300" p="0.5rem" textSize="body">
              Match Rate: {`${score ? score.toFixed(2) : '-'}%`}
            </Text>
          </Div>
          <Div
            pos="relative"
            maxH="calc(33% - 1.3rem)"
            h="calc(33% - 1.3rem)"
            w="100%"
            rounded={{ lg: 'lg', xs: '0' }}
            p={{ y: '1.25rem', x: '1.5rem' }}
            m={{ y: '1rem' }}
            border="2px solid"
            bg="white"
            borderColor="white"
            hoverBg="gray100"
            hoverBorderColor="brand100"
            cursor="pointer"
            overflow="auto"
            onMouseEnter={() => {
              hoverOn('ENGAGEMENT');
            }}
            onMouseLeave={() => {
              hoverOn('');
            }}
          >
            <Div d="flex" align="center">
              <Text fontFamily="secondary" textSize="subheader" textWeight="bold" p={{ y: '0.25rem' }} tag="span">
                Engagement Segments
              </Text>
              <Button w="2rem" h="2rem" tag="span" bg="gray500" rounded="circle" m={{ x: '1rem' }} />
            </Div>
            {numEngagementSegments > 0 ? (
              segmentsCategory.ENGAGEMENT.map(({ name, users: users_ }) => (
                <Text textWeight="300" p="0.5rem" textSize="body">
                  {`${name}: ${users_ || '-'} users`}
                </Text>
              ))
            ) : (
              <Text textWeight="300" p={{ t: '2rem' }} textSize="body">
                <i>There are no engagement segments</i>
              </Text>
            )}
          </Div>
          <Div
            pos="relative"
            maxH="calc(33% - 1.3rem)"
            h="calc(33% - 1.3rem)"
            w="100%"
            rounded="md"
            p={{ y: '1.25rem', x: '1.5rem' }}
            m={{ y: '1rem' }}
            border="2px solid"
            bg="white"
            borderColor="white"
            hoverBg="gray100"
            hoverBorderColor="brand100"
            cursor="pointer"
            overflow="auto"
            onMouseEnter={() => {
              hoverOn('PERSONALITY');
            }}
            onMouseLeave={() => {
              hoverOn('');
            }}
          >
            <Div d="flex" align="center">
              <Text fontFamily="secondary" textSize="subheader" textWeight="bold" p={{ y: '0.25rem' }} tag="span">
                Personality Segments
              </Text>
              <Button w="2rem" h="2rem" tag="span" bg="success500" rounded="circle" m={{ x: '1rem' }} />
            </Div>
            {numPersonalitySegments > 0 ? (
              segmentsCategory.PERSONALITY.map(({ name, users: users_ }) => (
                <Text textWeight="300" p="0.5rem" textSize="body">
                  {`${name}: ${users_ || '-'} users`}
                </Text>
              ))
            ) : (
              <Text textWeight="300" p={{ t: '2rem' }} textSize="body">
                <i>There are no personality segments</i>
              </Text>
            )}
          </Div>
        </Col>
        <Col size={{ xs: '6', lg: '8' }} h="100%">
          <Div
            h="calc(100% - 2rem)"
            w="100%"
            rounded={{ lg: 'lg', xs: '0' }}
            p={{ y: '1rem', x: '2rem' }}
            m={{ y: '1rem' }}
            style={styleOverride}
            d="flex"
            align="center"
            justify="center"
            pos="relative"
          >
            <HoverHelp hoveredOn={hoveredOn} />
          </Div>
        </Col>
      </Row>
    </Container>
  );
}

Datasets.propTypes = {
  segments: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      filter: PropTypes.string,
      users: PropTypes.number,
      matched: PropTypes.number,
      pxScores: pxScoresPropType,
    })
  ).isRequired,
  styleOverride: PropTypes.shape({}),
  minH: PropTypes.string,
  minW: PropTypes.string,
};

Datasets.defaultProps = {
  styleOverride: {
    background: Style.global.backgroundColor,
  },
  minH: '100vh',
  minW: '100vw',
};

export default Datasets;
